import { Temporal } from 'temporal-polyfill';
import { MultiMap } from './common-utils.js';
import { AvailabilityToDateTime, DayOfWeek, MinimalAvailability, TimeZoneAdjustment } from './data/index.js';
import { dayOfWeekFromTemporal, nextPlainDateOnDayOfWeek } from './scheduling-utils.js';

export function groupCoachAvailabilitiesByDay<T extends MinimalAvailability>(
  availabilities: T[],
): MultiMap<DayOfWeek, T> {
  return MultiMap.groupBy(availabilities, ({ day }) => day);
}

/**
 * Adjust a given availability by converting its plain time and day of week from the source time zone to the target time zone
 */
export function adjustAvailabilityByTimeZone(
  { day, startPlainTime, endPlainTime }: MinimalAvailability,
  { sourceTimeZone, targetTimeZone }: TimeZoneAdjustment,
): AvailabilityToDateTime {
  const plainDate = nextPlainDateOnDayOfWeek(Temporal.Now.plainDateISO('UTC'), day);

  const startZoned = plainDate.toZonedDateTime({ plainTime: startPlainTime, timeZone: sourceTimeZone });
  const endZoned = plainDate.toZonedDateTime({ plainTime: endPlainTime, timeZone: sourceTimeZone });

  const startWithTimeZone = startZoned.withTimeZone(targetTimeZone);
  const endWithTimeZone = endZoned.withTimeZone(targetTimeZone);

  const startAdjustedPlainDate = startWithTimeZone.toPlainDate();
  const endAdjustedPlainDate = endWithTimeZone.toPlainDate();

  const startAdjustedPlainTime = startWithTimeZone.toPlainTime();
  const endAdjustedPlainTime = endWithTimeZone.toPlainTime();

  return {
    startWithTimeZone,
    endWithTimeZone,
    startAdjustedPlainDate,
    endAdjustedPlainDate,
    startAdjustedPlainTime,
    endAdjustedPlainTime,
    adjustedDay: dayOfWeekFromTemporal(startAdjustedPlainDate),
  };
}
